import enrolLoyaltyAccount from 'actions/loyalty/enrolLoyaltyAccount'
import LayoutContainer from 'components/Common/LayoutContainer'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserEligibilityTier from 'loyalty/hooks/useLoyaltyUserEligibilityTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import LoyaltyProgramNameLogo from '../LoyaltyProgramNameLogo'
import LoyaltyTierPill from '../LoyaltyTierPill'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'

const Container = styled.div`
  padding-block: ${rem(12)};

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }
  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }
  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }
  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

function LoyaltyMembershipActivationPackageUpgradeBanner() {
  const [accountEligibleTier, accountEligibleTierLoading, accountEligibleTierError] = useLoyaltyUserEligibilityTier()
  const givenName = useAppSelector(state => state.auth.account.givenName)

  const dispatch = useAppDispatch()
  const activateMembership = useCallback(() => {
    dispatch(enrolLoyaltyAccount({ enrolment: 'existingUserRegistration' }))
  }, [dispatch])

  const pillMessage = useMemo(() => {
    switch (accountEligibleTier) {
      case 'silver':
        return 'New Silver benefit'
      case 'gold':
        return 'New Gold benefit'
      case 'bronze':
      case 'platinum':
      default:
        return ''
    }
  }, [accountEligibleTier])

  const coloredDescription = useMemo(() => {
    switch (accountEligibleTier) {
      case 'silver':
        return 'free room or package upgrade'
      case 'gold':
        return '2 free room or package upgrades'
      case 'bronze':
      case 'platinum':
      default:
        return ''
    }
  }, [accountEligibleTier])

  if (!accountEligibleTier || accountEligibleTierLoading || accountEligibleTierError || accountEligibleTier === 'platinum' || accountEligibleTier === 'bronze') return null

  return (
    <CSSBreakpoint min="tablet">
      <Container className={`tier-${accountEligibleTier}`}>
        <LayoutContainer>
          <Group direction="vertical" tabletDirection="horizontal" tabletHorizontalAlign="center" gap="8 20" verticalAlign="center" wrap="wrap">
            <Group direction="horizontal" gap={12} verticalAlign="center">
              <LoyaltyProgramNameLogo height={24} />
              <LoyaltyTierPill tier={accountEligibleTier} kind="primary">{pillMessage}</LoyaltyTierPill>
            </Group>
            <BodyText variant="medium">
              {!!givenName && <>{givenName}, you've got</>}
              {!givenName && <>You've got</>}
              {' '}
              <Colour value={`tier-${accountEligibleTier}-light-contrast`} as="b">
                {coloredDescription}
              </Colour>
              {' '}
              <TextLink size="medium" onClick={activateMembership}>Activate {getLoyaltyTierDisplayName(accountEligibleTier)} Status free</TextLink>
            </BodyText>
          </Group>
        </LayoutContainer>
      </Container>
    </CSSBreakpoint>
  )
}

export default LoyaltyMembershipActivationPackageUpgradeBanner
